import { BoundingBox, BoundingBoxContainment, BoundingBoxObject } from './BoundingBox';
import { vec3 } from 'gl-matrix';
import { ViewFrustum, FrumstumContainment } from './ViewFrustum';
import { Ray, RayTraceResult } from '../math';

export class Octree<T extends BoundingBoxObject> {
  public objects: Set<T>;
  public branches: Octree<T>[];

  private hasSplit: boolean;
  private canSplit: boolean;

  private constructor(
    public region: BoundingBox,
    public parent: Octree<T> | null,
    public objectMap: Map<T, Set<Octree<T>>>,
    private minimumSize: number,
  ) {
    this.objects = new Set<T>();
    this.branches = [];
    this.hasSplit = false;
    this.canSplit = true;
  }

  public static create<T extends BoundingBoxObject>(
    region: BoundingBox,
    minimumSize: number = 10.0,
  ) {
    return new Octree<T>(region, null, new Map<T, Set<Octree<T>>>(), minimumSize);
  }

  public prune(): boolean {
    if (this.hasSplit) {
      let anyAlive = false;
      // It's not a leaf node so check all child nodes.
      for (let branch of this.branches) {
        if (!branch.prune()) {
          anyAlive = true;
          break;
        }
      }

      if (!anyAlive) {
        // All branches are dead so they can be removed.
        this.branches = [];
        this.hasSplit = false;

        return true;
      } else {
        return false;
      }
    } else {
      // If this leaf node is empty return true.
      return this.objects.size === 0;
    }
  }

  public remove(obj: T): T {
    this.objectMap.get(obj)?.delete(this);
    this.objects.delete(obj);
    return obj;
  }

  /**
   * Remove an object from every node it lives in. After {@link distribute},
   * objects descend from the root into one or more leaves; calling
   * {@link remove} only on the root would leave dangling leaf references. This
   * walks the object's full membership set via {@link objectMap}.
   *
   * Safe to call on any node — the map is shared across the whole tree.
   */
  public removeEverywhere(obj: T): void {
    const containers = this.objectMap.get(obj);
    if (!containers) return;
    // Snapshot before iterating: node.remove() mutates the same set.
    const snapshot = [...containers];
    for (const node of snapshot) {
      node.remove(obj);
    }
    this.objectMap.delete(obj);
  }

  public insert(obj: T, deferDistribution?: boolean): void {
    this.objects.add(obj);

    if (this.objectMap.has(obj)) {
      this.objectMap.get(obj)?.add(this);
    } else {
      this.objectMap.set(obj, new Set([this]));
    }
    if (!deferDistribution) {
      this.distribute();
    }
  }

  public insertArray(objArray: T[], deferDistribution?: boolean): void {
    for (let obj of objArray) {
      this.insert(obj, true);
    }

    if (!deferDistribution) {
      this.distribute();
    }
  }

  public distribute(): void {
    if (!this.split()) {
      // Node was not split (for reasons see the split method) so do nothing,
      // this node will be a leaf node.
      return;
    }

    // Node was split, attempt to add the objects to one of the child nodes.

    // Keep track of the branches that get modified so we can recurively
    // trigger a distribute call on them.
    let modifiedBranches = new Set<Octree<T>>();

    // Loop over all objects (if any) and add them to the appropriate child
    // nodes.
    this.objects.forEach((obj) => {
      let bounds = obj.getBoundingBox();
      let passedToBranch = false;

      for (let branch of this.branches) {
        let containment = bounds.isContained(branch.region);
        let parentContainment = branch.region.isContained(bounds);

        if (
          containment == BoundingBoxContainment.Full ||
          parentContainment == BoundingBoxContainment.Full
        ) {
          // Object is fully contained so no need to check further trees
          branch.insert(obj, true);
          passedToBranch = true;

          modifiedBranches.add(branch);
          // No need to check further as the object is contained in this
          // region.
          break;
        } else if (containment == BoundingBoxContainment.Partial) {
          // Object is only partially contained so add to this tree but
          // also search for others.
          branch.insert(obj, true);
          passedToBranch = true;

          modifiedBranches.add(branch);
        }
      }

      // If the object is now contained in a branch(es) we can remove it from the parent
      if (passedToBranch) {
        this.remove(obj);
      }
    });

    // Recursively call distribute for the modified branches.
    modifiedBranches.forEach((branch) => branch.distribute());
  }

  private split(): boolean {
    if (this.hasSplit) {
      // We have already split, this tree can no longer be considered a leaf node
      // and should have 0 objects. Any inserts should be done in the child nodes.
      return true;
    }

    if (!this.canSplit) {
      // This node can not be split up any further due to children being smaller
      // than the minimum size.
      return false;
    }

    if (this.objects.size <= 1) {
      // When there is only 0 or 1 objects we don't need to divide any further, this
      // node can remain a leaf node.
      return false;
    }

    let sMin = this.region.min;
    let sMax = this.region.max;

    let center = vec3.subtract(vec3.create(), sMax, sMin);
    vec3.divide(center, center, [2, 2, 2]);

    if (
      center[0] < this.minimumSize &&
      center[1] < this.minimumSize &&
      center[2] < this.minimumSize
    ) {
      // When the division results in a distance less than minimumSize on all axis
      // we stop as at some point it'll be more efficient to brute force intersect
      // than maintaining the amount of brances of the tree. Ideally there will not
      // be many objects on a single position.
      this.canSplit = false; // Cache the check value so we don't have to repeat it
      return false;
    }

    vec3.add(center, sMin, center);

    let regions = new Array<BoundingBox>(
      new BoundingBox(vec3.clone(sMin), vec3.clone(center)),
      new BoundingBox(
        vec3.fromValues(center[0], sMin[1], sMin[2]),
        vec3.fromValues(sMax[0], center[1], center[2]),
      ),
      new BoundingBox(
        vec3.fromValues(center[0], sMin[1], center[2]),
        vec3.fromValues(sMax[0], center[1], sMax[2]),
      ),
      new BoundingBox(
        vec3.fromValues(sMin[0], sMin[1], center[2]),
        vec3.fromValues(center[0], center[1], sMax[2]),
      ),

      new BoundingBox(
        vec3.fromValues(sMin[0], center[1], sMin[2]),
        vec3.fromValues(center[0], sMax[1], center[2]),
      ),
      new BoundingBox(
        vec3.fromValues(center[0], center[1], sMin[2]),
        vec3.fromValues(sMax[0], sMax[1], center[2]),
      ),
      new BoundingBox(
        vec3.fromValues(sMin[0], center[1], center[2]),
        vec3.fromValues(center[0], sMax[1], sMax[2]),
      ),
      new BoundingBox(vec3.clone(center), vec3.clone(sMax)),
    );

    this.branches = regions.map(
      (region) => new Octree(region, this, this.objectMap, this.minimumSize),
    );

    this.hasSplit = true;

    return true;
  }

  public rayTrace(ray: Ray): RayTraceResult[] {
    const hits = new Set<any>();
    const intersections = new Map();

    let rayTraceImpl = (tree: Octree<T>) => {
      if (tree.region.intersectsRay(ray)) {
        tree.objects.forEach((obj) => {
          // The picking manager calls the ray trace method intensively, make sure that all getBoundingBox() implementations cache their value
          if (obj.getBoundingBox().intersectsRay(ray)) {
            if (obj.canIntersectRay()) {
              let intersection = obj.intersectsRay(ray);
              if (intersection) {
                intersections.set(obj, intersection);
                hits.add(obj);
              }
            } else {
              hits.add(obj);
            }
          }
        });

        if (tree.hasSplit) {
          for (let branch of tree.branches) {
            rayTraceImpl(branch);
          }
        }
      }
    };

    rayTraceImpl(this);

    let results: RayTraceResult[] = [];
    hits.forEach((obj: any) => {
      results.push({
        ray: ray,
        object: obj,
        intersection: intersections.has(obj) ? intersections.get(obj) : null,
        distance: obj.getPosition ? vec3.distance(obj.getPosition(), ray.origin) : 0,
      });
    });

    results.sort((a, b) => {
      return a.distance - b.distance;
    });

    return results;
  }

  public getObjectsInRegion(boundingBox: BoundingBox): T[] {
    let objects = new Set<T>();
    let search = (tree: Octree<T>) => {
      if (tree.hasSplit) {
        for (let branch of tree.branches) {
          let containment = branch.region.isContained(boundingBox);

          if (containment == BoundingBoxContainment.None) {
            continue; // No containment at all, skip to next
          } else if (containment == BoundingBoxContainment.Partial) {
            // Partial containment, recurse
            search(branch);
          } else {
            // full containment, get all values, without checking bounding boxes
            let walkTree = (t: Octree<T>) => {
              if (t.hasSplit) {
                for (let b of t.branches) {
                  walkTree(b);
                }
              } else {
                t.objects.forEach((obj) => {
                  objects.add(obj);
                });
              }
            };
            walkTree(tree);
          }
        }
      } else {
        // iterate values and check boundingbox
        tree.objects.forEach((obj) => {
          if (obj.getBoundingBox().isContained(boundingBox) != BoundingBoxContainment.None) {
            objects.add(obj);
          }
        });
      }
    };

    search(this);

    return [...objects];
  }

  // Pre-allocated result array for frustum queries (avoids per-frame allocation).
  // Dedup of objects straddling multiple leaves uses a per-query visit tag
  // stamped onto each entry — bumped at query start, compared on visit.
  // Cheaper than Set.add (one numeric compare instead of hash + bucket walk)
  // and avoids the per-call forEach callback overhead.
  private frustumResultArray: T[] = [];
  private currentFrustumQueryTag = 0;

  public getObjectsInFrustum(frustum: ViewFrustum): T[] {
    // Bump the query tag, skipping 0 because that's the implicit default for
    // any T that hasn't initialised _octreeVisitTag.
    this.currentFrustumQueryTag = (this.currentFrustumQueryTag + 1) | 0;
    if (this.currentFrustumQueryTag === 0) this.currentFrustumQueryTag = 1;

    const result = this.frustumResultArray;
    result.length = 0;
    this.frustumSearchImpl(this, frustum, result);
    return result;
  }

  private frustumSearchImpl(tree: Octree<T>, frustum: ViewFrustum, result: T[]): void {
    if (tree.hasSplit) {
      for (const branch of tree.branches) {
        const containment = frustum.containsBoundingBox(branch.region);
        if (containment === FrumstumContainment.None) continue;
        if (containment === FrumstumContainment.Partial) {
          this.frustumSearchImpl(branch, frustum, result);
        } else {
          // Full containment: every object inside this subtree is guaranteed
          // visible; skip per-object frustum tests.
          this.collectAll(branch, result);
        }
      }
      return;
    }
    const tag = this.currentFrustumQueryTag;
    for (const obj of tree.objects) {
      if (obj._octreeVisitTag === tag) continue;
      // Stamp before testing so a rejected object isn't re-tested in another
      // straddled leaf — its bbox doesn't change between visits.
      obj._octreeVisitTag = tag;
      if (frustum.containsBoundingBox(obj.getBoundingBox()) !== FrumstumContainment.None) {
        result.push(obj);
      }
    }
  }

  private collectAll(tree: Octree<T>, result: T[]): void {
    if (tree.hasSplit) {
      for (const b of tree.branches) {
        this.collectAll(b, result);
      }
      return;
    }
    const tag = this.currentFrustumQueryTag;
    for (const obj of tree.objects) {
      if (obj._octreeVisitTag === tag) continue;
      obj._octreeVisitTag = tag;
      result.push(obj);
    }
  }
}
